import type { MouseEvent, ReactNode } from "react";

import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import type { CustomKnowledge } from "domains/customContentAndSources/types";
import { KnowledgeDataItemBrowseView } from "domains/knowledgeData/components/KnowledgeDataItemBrowseView";
import { KnowledgeDataItemLatestView } from "domains/knowledgeData/components/KnowledgeDataItemLatestView";
import { KnowledgeDataNavigationalItemLatestView } from "domains/knowledgeData/components/KnowledgeDataNavigationalItemLatestView";
import { KnowledgeDataNavigationItemBrowseView } from "domains/knowledgeData/components/KnowledgeDataNavigationItemBrowseView";
import { useMedia } from "domains/viewport/useMedia";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";
import type { Knowledge } from "entities/knowledge/types";

type InteractiveDataItemProps = {
  articleAnnouncement?: ReactNode;
  Component: typeof KnowledgeDataItemBrowseView | typeof KnowledgeDataItemLatestView;
  knowledge: Knowledge | CustomKnowledge;
  link: string;
};

const onDetailLinkClick = (event: MouseEvent) => {
  event.stopPropagation();
};

const InteractiveDataItem = ({ Component, link, knowledge, articleAnnouncement }: InteractiveDataItemProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const isSmallScreen = useMedia({ small: true, medium: true }, false);

  const { id, title, interactive_link } = knowledge;

  const updateMediaPlayer = () => {
    if (isSmallScreen) {
      go(link);
      return;
    }
    dispatch(
      setVisualMediaPlayer({
        id: id,
        name: title,
        url: interactive_link ?? undefined,
        mediaType: MediaType.knowledgeData,
      }),
    );
  };

  return (
    <Component
      onClick={updateMediaPlayer}
      knowledge={knowledge}
      link={link}
      onDetailLinkClick={onDetailLinkClick}
      articleAnnouncement={articleAnnouncement}
    />
  );
};

type DataItemProps = {
  articleAnnouncement?: ReactNode;
  knowledge: Knowledge | CustomKnowledge;
  latestView?: boolean;
  link: string;
  showKnowledgeTypeHeader?: boolean;
};

export const DataItem = ({ knowledge, link, latestView = false, articleAnnouncement }: DataItemProps) => {
  if (knowledge.interactive_link) {
    const componentToRender = latestView ? KnowledgeDataItemLatestView : KnowledgeDataItemBrowseView;
    return <InteractiveDataItem Component={componentToRender} link={link} knowledge={knowledge} />;
  }
  if (latestView) {
    return (
      <KnowledgeDataNavigationalItemLatestView
        link={link}
        knowledge={knowledge}
        articleAnnouncement={articleAnnouncement}
      />
    );
  }

  return <KnowledgeDataNavigationItemBrowseView knowledge={knowledge} link={link} showCaret />;
};
